import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip
} from '@mui/material';
import { Search, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Close, Transform as TransformIcon, Refresh as RefreshIcon, FilterList } from '@mui/icons-material';
import { partService } from '../services/partService';
import { useAuth } from '../hooks/useAuth';
import NewPartModal from '../components/NewPartModal';
import PartEditDialog from '../components/PartEditDialog';
import PartLabelChips from '../components/PartLabelChips';
import { formatCurrency } from '../components/CurrencyTextField';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { procurementSummaryFieldsRowSx } from '../components/procurement/ProcurementSummaryBar';
import { useResizableColumns } from '../hooks/useResizableColumns';
import usePersistedListFilters from '../hooks/usePersistedListFilters';
import { effectiveServiceCode } from '../utils/serviceCodeDisplay';

const UNCATEGORIZED_FILTER = '__uncategorized__';

const PARTS_LIST_HEADER_FILTERS_INITIAL = {
  partNo: '',
  desc: '',
  status: '',
  qty: '',
  min: '',
  unitPrice: '',
  quotes: '',
  orders: '',
  invoices: '',
  slips: '',
  category: '',
  labels: '',
  supplier: ''
};

const PARTS_LIST_FILTERS_DEFAULTS = {
  searchTerm: '',
  categoryFilter: '',
  labelFilter: '',
  headerColFilters: { ...PARTS_LIST_HEADER_FILTERS_INITIAL },
  page: 0
};

function trimHeaderFilterObj(obj) {
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, String(v ?? '').trim()]));
}

/** Maps debounced header keys to GET /api/parts query params. */
function partsColumnFilterQueryParams(d) {
  const out = {};
  if (d.partNo) out.filter_part_number = d.partNo;
  if (d.desc) out.filter_description = d.desc;
  if (d.status) out.filter_status = d.status;
  if (d.qty) out.filter_qty = d.qty;
  if (d.min) out.filter_min = d.min;
  if (d.unitPrice) out.filter_unit_price = d.unitPrice;
  if (d.quotes) out.filter_quotes = d.quotes;
  if (d.orders) out.filter_orders = d.orders;
  if (d.invoices) out.filter_invoices = d.invoices;
  if (d.slips) out.filter_slips = d.slips;
  if (d.category) out.filter_category = d.category;
  if (d.labels) out.filter_labels = d.labels;
  if (d.supplier) out.filter_supplier = d.supplier;
  return out;
}

const partsColHeaderStackSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.375,
  width: '100%',
  pr: 0
};

const partsColHeaderLabelSlotSx = {
  flexShrink: 0,
  width: '100%',
  minHeight: '1.5rem',
  overflow: 'hidden'
};

const partsColHeaderCaptionInSlotSx = {
  fontSize: '0.65rem',
  fontWeight: 700,
  lineHeight: 1.25,
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%'
};

const partsHeaderFilterTextFieldSx = {
  width: '100%',
  flexShrink: 0,
  '& .MuiInputBase-root': { fontSize: '0.7rem', py: 0.1 },
  '& .MuiInputBase-input': { py: 0.35 }
};

/** Words `inactive` or `deactivated` in the search box limit results to deactivated parts; other words are used as text search (description, supplier, part #). */
function parsePartsSearchQuery(raw) {
  const trimmed = raw.trim();
  if (!trimmed) return { textSearch: '', inactiveOnly: false };
  const tokens = trimmed.split(/\s+/);
  let inactiveOnly = false;
  const kept = [];
  for (const t of tokens) {
    const low = t.toLowerCase();
    if (low === 'inactive' || low === 'deactivated') {
      inactiveOnly = true;
    } else {
      kept.push(t);
    }
  }
  return { textSearch: kept.join(' ').trim(), inactiveOnly };
}

const Parts = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusPartIdRaw = searchParams.get('part_id');
  const focusPartNumberRaw = searchParams.get('part_number');
  const focusPartId =
    focusPartIdRaw != null && String(focusPartIdRaw).trim() !== ''
      ? parseInt(String(focusPartIdRaw).trim(), 10)
      : null;
  const focusPartIdValid = Number.isFinite(focusPartId) ? focusPartId : null;
  const { isAdmin } = useAuth();
  const DEFAULT_COL_WIDTHS = {
    partNo: 130,
    desc: 220,
    status: 88,
    qty: 90,
    min: 90,
    unitPrice: 120,
    quotes: 80,
    orders: 80,
    invoices: 80,
    slips: 90,
    category: 110,
    labels: 160,
    supplier: 140
  };
  const {
    values: listFilters,
    setField: setListFilter,
    clear: clearListFilters,
    hasActiveFilters
  } = usePersistedListFilters('wm_parts_list_filters', PARTS_LIST_FILTERS_DEFAULTS);
  const searchTerm = listFilters.searchTerm;
  const setSearchTerm = useCallback((v) => setListFilter('searchTerm', v), [setListFilter]);
  const categoryFilter = listFilters.categoryFilter;
  const setCategoryFilter = useCallback((v) => setListFilter('categoryFilter', v), [setListFilter]);
  const labelFilter = listFilters.labelFilter;
  const setLabelFilter = useCallback((v) => setListFilter('labelFilter', v), [setListFilter]);
  const headerColFilters = listFilters.headerColFilters;
  const setHeaderColFilters = useCallback((v) => setListFilter('headerColFilters', v), [setListFilter]);
  const page = listFilters.page;
  const setPage = useCallback((v) => setListFilter('page', v), [setListFilter]);
  const handleClearFilters = useCallback(() => {
    clearListFilters();
    if (focusPartIdRaw || focusPartNumberRaw) {
      const next = new URLSearchParams(searchParams);
      next.delete('part_id');
      next.delete('part_number');
      setSearchParams(next, { replace: true });
    }
  }, [clearListFilters, focusPartIdRaw, focusPartNumberRaw, searchParams, setSearchParams]);
  const [categories, setCategories] = useState([]);
  const [labelsCatalog, setLabelsCatalog] = useState([]);
  const [debouncedHeaderColFilters, setDebouncedHeaderColFilters] = useState(() =>
    trimHeaderFilterObj(headerColFilters || PARTS_LIST_HEADER_FILTERS_INITIAL)
  );
  const [showInactiveParts, setShowInactiveParts] = useState(() => {
    try {
      return window.localStorage.getItem('wm_parts_show_inactive') === 'true';
    } catch {
      return false;
    }
  });
  const [parts, setParts] = useState([]);
  const [partsTotal, setPartsTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const raw = parseInt(window.localStorage.getItem('wm_parts_rows_per_page'), 10);
      return [50, 100, 200].includes(raw) ? raw : 50;
    } catch {
      return 50;
    }
  });
  const [loading, setLoading] = useState(true);
  const partsFilterKeyRef = useRef(null);
  const [error, setError] = useState('');
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertServiceCode, setConvertServiceCode] = useState('');
  const [convertSaving, setConvertSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [highlightPartId, setHighlightPartId] = useState(focusPartIdValid);
  const didApplyFocusFromUrl = useRef(false);
  const { colWidths, startResize } = useResizableColumns({
    defaultWidths: DEFAULT_COL_WIDTHS,
    storageKey: 'wm_parts_col_widths',
    minWidths: {
      partNo: 100,
      desc: 140,
      status: 72,
      qty: 70,
      min: 70,
      unitPrice: 100,
      quotes: 70,
      orders: 70,
      invoices: 70,
      slips: 80,
      category: 100,
      labels: 120,
      supplier: 120
    }
  });

  const loadCategories = useCallback(async () => {
    try {
      const res = await partService.getCategories();
      setCategories(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Error loading part categories:', err);
    }
  }, []);

  const loadLabelsCatalog = useCallback(async () => {
    try {
      const res = await partService.getLabels();
      setLabelsCatalog(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Error loading part labels:', err);
    }
  }, []);

  const loadParts = useCallback(async () => {
    const { textSearch, inactiveOnly } = parsePartsSearchQuery(searchTerm);
    const filterKey = [
      textSearch,
      String(inactiveOnly),
      String(showInactiveParts),
      JSON.stringify(debouncedHeaderColFilters),
      categoryFilter,
      labelFilter,
      String(highlightPartId)
    ].join('|');
    const filterJustChanged =
      partsFilterKeyRef.current != null && partsFilterKeyRef.current !== filterKey;
    partsFilterKeyRef.current = filterKey;

    const effectivePage = filterJustChanged ? 0 : page;
    if (filterJustChanged && page !== 0) {
      setPage(0);
    }

    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      const statusNeedle = String(debouncedHeaderColFilters.status || '').toLowerCase();
      const statusNeedsInactive =
        statusNeedle.includes('inact') || statusNeedle.includes('deact');
      const params = {
        search: textSearch || undefined,
        limit: rowsPerPage,
        offset: effectivePage * rowsPerPage,
        inactive_only: inactiveOnly ? 'true' : undefined,
        activeOnly:
          highlightPartId != null
            ? 'false'
            : inactiveOnly
              ? 'false'
              : showInactiveParts || statusNeedsInactive
                ? 'false'
                : 'true',
        category: categoryFilter || undefined,
        label_id: labelFilter || undefined,
        ...partsColumnFilterQueryParams(debouncedHeaderColFilters)
      };
      const res = await partService.getAll(params);
      const list = res.data ?? [];
      const total =
        typeof res?.total === 'number'
          ? res.total
          : typeof res?.pagination?.total === 'number'
            ? res.pagination.total
            : list.length;
      const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
      if (effectivePage > maxPage) {
        setPage(maxPage);
        return;
      }
      setPartsTotal(total);
      setParts(list);
      let nextSelected = list.length > 0 ? list[0] : null;
      if (highlightPartId != null) {
        const match = list.find(
          (row) => row.id === highlightPartId || String(row.id) === String(highlightPartId)
        );
        if (match) nextSelected = match;
      }
      setSelectedPart(nextSelected);
    } catch (err) {
      console.error('Error loading parts:', err);
      setError(err.response?.data?.message || 'Failed to load parts');
    } finally {
      setLoading(false);
    }
  }, [
    searchTerm,
    showInactiveParts,
    debouncedHeaderColFilters,
    highlightPartId,
    categoryFilter,
    labelFilter,
    page,
    rowsPerPage
  ]);

  useEffect(() => {
    if (didApplyFocusFromUrl.current || focusPartIdValid == null) return;
    didApplyFocusFromUrl.current = true;
    setHighlightPartId(focusPartIdValid);
    const partNumber =
      focusPartNumberRaw != null && String(focusPartNumberRaw).trim() !== ''
        ? String(focusPartNumberRaw).trim()
        : '';
    if (partNumber) {
      setSearchTerm(partNumber);
      return;
    }
    partService
      .getById(focusPartIdValid)
      .then((res) => {
        const part = res?.data ?? res;
        const num =
          part?.part_number != null && String(part.part_number).trim() !== ''
            ? String(part.part_number).trim()
            : '';
        if (num) {
          setSearchTerm(num);
        }
      })
      .catch(() => {});
  }, [focusPartIdValid, focusPartNumberRaw, setSearchTerm]);

  useEffect(() => {
    if (highlightPartId == null || loading) return;
    const match = parts.find(
      (row) => row.id === highlightPartId || String(row.id) === String(highlightPartId)
    );
    if (!match) return;
    setSelectedPart(match);
    window.requestAnimationFrame(() => {
      document.getElementById(`parts-row-${match.id}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }, [highlightPartId, loading, parts]);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = trimHeaderFilterObj(headerColFilters);
      setDebouncedHeaderColFilters((prev) => {
        const same = Object.keys(PARTS_LIST_HEADER_FILTERS_INITIAL).every((k) => prev[k] === next[k]);
        return same ? prev : next;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [headerColFilters]);

  useEffect(() => {
    loadCategories();
    loadLabelsCatalog();
  }, [loadCategories, loadLabelsCatalog]);

  useEffect(() => {
    loadParts();
  }, [loadParts]);

  const handleSearch = () => {
    loadParts();
  };

  const openNewDialog = () => {
    setNewDialogOpen(true);
    setError('');
  };

  const openEditDialog = (row) => {
    if (!row) return;
    setSelectedPart(row);
    setEditDialogOpen(true);
    setError('');
  };

  const handleDeletePart = async () => {
    if (!selectedPart?.id) return;
    try {
      setSaving(true);
      setError('');
      await partService.delete(selectedPart.id);
      setDeleteConfirmOpen(false);
      setSelectedPart(null);
      loadParts();
    } catch (err) {
      console.error('Error deleting part:', err);
      setError(err.response?.data?.message || 'Failed to deactivate part');
    } finally {
      setSaving(false);
    }
  };

  const handleReactivate = async () => {
    if (!selectedPart?.id) return;
    try {
      setSaving(true);
      setError('');
      const res = await partService.update(selectedPart.id, { is_active: true });
      const updated = res?.data;
      if (updated?.id) {
        setSelectedPart({ ...selectedPart, ...updated, is_active: true });
        setHighlightPartId(updated.id);
      }
      setSuccessMessage(`Part ${updated?.part_number ?? selectedPart.part_number ?? selectedPart.id} reactivated.`);
      await loadParts();
    } catch (err) {
      console.error('Error reactivating part:', err);
      setError(err.response?.data?.message || 'Failed to reactivate part');
    } finally {
      setSaving(false);
    }
  };

  const openConvertDialog = () => {
    if (!selectedPart) return;
    setConvertServiceCode(
      selectedPart.part_number != null && String(selectedPart.part_number).trim() !== ''
        ? String(selectedPart.part_number).trim()
        : ''
    );
    setConvertDialogOpen(true);
    setError('');
  };

  const handleConvertToService = async () => {
    if (!selectedPart?.id) return;
    try {
      setConvertSaving(true);
      setError('');
      const body = {};
      const t = convertServiceCode.trim();
      if (t) body.service_code = t;
      const res = await partService.convertToService(selectedPart.id, body);
      const svc = res.data?.service;
      const u = res.data?.updated || {};
      setConvertDialogOpen(false);
      setSuccessMessage(
        `Created service "${effectiveServiceCode(svc)}" (ID ${svc?.id}). Updated quote lines: ${u.quote_items}, order lines: ${u.work_order_items}, invoice lines: ${u.customer_invoice_items}, packing slip rows: ${u.packing_slip_items}. The part was deactivated.`
      );
      await loadParts();
    } catch (err) {
      console.error('Convert part to service:', err);
      const d = err.response?.data;
      let msg = d?.message || err.message || 'Failed to convert part';
      if (d?.blocking) {
        const bits = Object.entries(d.blocking)
          .filter(([, v]) => v > 0)
          .map(([k, v]) => `${k}: ${v}`);
        if (bits.length) msg += ` (${'; '.join(bits)})`;
      }
      setError(msg);
    } finally {
      setConvertSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Parts
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" color="primary" size="small" startIcon={<EditIcon />} onClick={() => selectedPart && openEditDialog(selectedPart)} disabled={!selectedPart}>
            Edit
          </Button>
          {selectedPart?.is_active === false ? (
            <Button
              variant="outlined"
              color="success"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleReactivate}
              disabled={!selectedPart || saving}
            >
              Reactivate
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => selectedPart && setDeleteConfirmOpen(true)}
              disabled={!selectedPart}
            >
              Deactivate
            </Button>
          )}
          {isAdmin() && (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<TransformIcon />}
              onClick={() => selectedPart && openConvertDialog()}
              disabled={!selectedPart || selectedPart?.is_active === false}
            >
              Change to Service
            </Button>
          )}
          <Button variant="contained" color="success" size="small" startIcon={<AddIcon />} onClick={openNewDialog}>
            New
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Selected Part Information Bar */}
      <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: 'surfaceMuted.main', border: 1, borderColor: 'borderSubtle.main' }}>
        <Box sx={procurementSummaryFieldsRowSx()}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Part #</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedPart?.part_number ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Status</Typography>
            {selectedPart?.is_active === false ? (
              <Chip label="Inactive" size="small" color="default" variant="outlined" sx={{ height: 22, fontSize: '0.7rem', mt: 0.25 }} />
            ) : (
              <Typography variant="body2">Active</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Qty</Typography>
            <Typography variant="body2">
              {selectedPart?.quantity_on_hand != null ? selectedPart.quantity_on_hand : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Min</Typography>
            <Typography variant="body2">
              {selectedPart?.minimum_quantity != null ? selectedPart.minimum_quantity : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Unit Price</Typography>
            <Typography variant="body2">
              {selectedPart ? formatCurrency(selectedPart.unit_price) : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Category</Typography>
            <Typography variant="body2">
              {selectedPart?.category ?? '—'}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 120 }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Labels</Typography>
            <PartLabelChips
              labels={selectedPart?.labels}
              onClickLabel={(lab) => setLabelFilter(String(lab.id))}
            />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Supplier</Typography>
            <Typography variant="body2">
              {selectedPart?.supplier ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Quotes</Typography>
            <Typography variant="body2">
              {(selectedPart?.quote_references ?? 0) > 0 ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/quotes?part_id=${selectedPart.id}`)}
                >
                  {selectedPart.quote_references}
                </Button>
              ) : (
                0
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Orders</Typography>
            <Typography variant="body2">
              {(selectedPart?.order_references ?? 0) > 0 ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/work-orders?part_id=${selectedPart.id}`)}
                >
                  {selectedPart.order_references}
                </Button>
              ) : (
                0
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Invoices</Typography>
            <Typography variant="body2">
              {(selectedPart?.invoice_references ?? 0) > 0 ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/customer-invoices?part_id=${selectedPart.id}`)}
                >
                  {selectedPart.invoice_references}
                </Button>
              ) : (
                0
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Packing Slips</Typography>
            <Typography variant="body2">
              {(selectedPart?.packing_slip_references ?? 0) > 0 ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/packing-slips?part_id=${selectedPart.id}`)}
                >
                  {selectedPart.packing_slip_references}
                </Button>
              ) : (
                0
              )}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 150 }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Description</Typography>
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedPart?.part_description ?? selectedPart?.description ?? '—'}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setSelectedPart(null)}
            disabled={!selectedPart}
            sx={{ ml: 'auto' }}
            title="Clear selection"
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      <Paper sx={{ p: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search description, supplier, part #, label — add inactive or deactivated to list only deactivated parts"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchTerm ? (
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => setSearchTerm('')}
                      edge="end"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  ) : null}
                  <IconButton size="small" edge="end" onClick={handleSearch}>
                    <Search />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 280 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="parts-category-filter-label">Category</InputLabel>
            <Select
              labelId="parts-category-filter-label"
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">All categories</MenuItem>
              <MenuItem value={UNCATEGORIZED_FILTER}>Uncategorized</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="parts-label-filter-label" shrink>
              Label
            </InputLabel>
            <Select
              labelId="parts-label-filter-label"
              label="Label"
              notched
              displayEmpty
              value={labelFilter}
              onChange={(e) => setLabelFilter(e.target.value)}
              renderValue={(selected) => {
                if (!selected) return 'All labels';
                const lab = labelsCatalog.find((l) => String(l.id) === String(selected));
                return lab?.name || 'All labels';
              }}
            >
              <MenuItem value="">All labels</MenuItem>
              {labelsCatalog.map((lab) => (
                <MenuItem key={lab.id} value={String(lab.id)}>
                  {lab.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={(
              <Checkbox
                checked={showInactiveParts}
                onChange={(e) => {
                  const v = e.target.checked;
                  setShowInactiveParts(v);
                  try {
                    window.localStorage.setItem('wm_parts_show_inactive', v ? 'true' : 'false');
                  } catch {
                    /* ignore */
                  }
                }}
                size="small"
              />
            )}
            label={<Typography variant="body2">Show deactivated parts</Typography>}
          />
          {(hasActiveFilters || focusPartIdRaw || focusPartNumberRaw) ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterList />}
              onClick={handleClearFilters}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear filters
            </Button>
          ) : null}
          <Typography variant="body2" sx={{ color: 'text.secondary', ml: { sm: 'auto' } }}>
            {partsTotal === 0
              ? 'No parts match the current filters.'
              : parts.length === 0
                ? `Page ${page + 1} · ${partsTotal} part${partsTotal === 1 ? '' : 's'} total`
                : `Showing ${page * rowsPerPage + 1}–${page * rowsPerPage + parts.length} of ${partsTotal} part${partsTotal === 1 ? '' : 's'}`}
          </Typography>
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
        <Table
          size="small"
          stickyHeader
          sx={{
            '& .MuiTableCell-head': { verticalAlign: 'top' },
            '& .MuiTableHead-root .MuiTableCell-stickyHeader': {
              top: -13,
              zIndex: 20,
              backgroundColor: 'background.paper',
              backgroundImage: 'none',
              backgroundClip: 'padding-box',
              opacity: 1,
              overflow: 'hidden',
              borderTop: '1px solid',
              borderTopColor: 'background.paper',
              boxShadow: (theme) =>
                `inset 0 -1px 0 rgba(224,224,224,0.9), 0 -13px 0 0 ${theme.palette.background.paper}`
            }
          }}
        >
          <TableHead>
            <TableRow>
              <ResizableHeaderCell width={colWidths.partNo} onResizeStart={(e) => startResize('partNo', e)}>
                <Box sx={partsColHeaderStackSx}>
                  <Box sx={partsColHeaderLabelSlotSx}>
                    <Typography variant="caption" sx={partsColHeaderCaptionInSlotSx}>Part #</Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.partNo}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, partNo: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={partsHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.desc} onResizeStart={(e) => startResize('desc', e)}>
                <Box sx={partsColHeaderStackSx}>
                  <Box sx={partsColHeaderLabelSlotSx}>
                    <Typography variant="caption" sx={partsColHeaderCaptionInSlotSx}>Part Description</Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.desc}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, desc: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={partsHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.status} onResizeStart={(e) => startResize('status', e)}>
                <Box sx={partsColHeaderStackSx}>
                  <Box sx={partsColHeaderLabelSlotSx}>
                    <Typography variant="caption" sx={partsColHeaderCaptionInSlotSx}>Status</Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.status}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, status: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={partsHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.qty} onResizeStart={(e) => startResize('qty', e)}>
                <Box sx={partsColHeaderStackSx}>
                  <Box sx={partsColHeaderLabelSlotSx}>
                    <Typography variant="caption" sx={partsColHeaderCaptionInSlotSx}>Qty</Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.qty}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, qty: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={partsHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.min} onResizeStart={(e) => startResize('min', e)}>
                <Box sx={partsColHeaderStackSx}>
                  <Box sx={partsColHeaderLabelSlotSx}>
                    <Typography variant="caption" sx={partsColHeaderCaptionInSlotSx}>Min</Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.min}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, min: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={partsHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.unitPrice} onResizeStart={(e) => startResize('unitPrice', e)}>
                <Box sx={partsColHeaderStackSx}>
                  <Box sx={partsColHeaderLabelSlotSx}>
                    <Typography variant="caption" sx={partsColHeaderCaptionInSlotSx}>Unit Price</Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.unitPrice}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, unitPrice: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={partsHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.quotes} onResizeStart={(e) => startResize('quotes', e)}>
                <Box sx={partsColHeaderStackSx}>
                  <Box sx={partsColHeaderLabelSlotSx}>
                    <Typography variant="caption" sx={partsColHeaderCaptionInSlotSx}>Quotes</Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.quotes}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, quotes: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={partsHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.orders} onResizeStart={(e) => startResize('orders', e)}>
                <Box sx={partsColHeaderStackSx}>
                  <Box sx={partsColHeaderLabelSlotSx}>
                    <Typography variant="caption" sx={partsColHeaderCaptionInSlotSx}>Orders</Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.orders}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, orders: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={partsHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.invoices} onResizeStart={(e) => startResize('invoices', e)}>
                <Box sx={partsColHeaderStackSx}>
                  <Box sx={partsColHeaderLabelSlotSx}>
                    <Typography variant="caption" sx={partsColHeaderCaptionInSlotSx}>Invoices</Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.invoices}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, invoices: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={partsHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.slips} onResizeStart={(e) => startResize('slips', e)}>
                <Box sx={partsColHeaderStackSx}>
                  <Box sx={partsColHeaderLabelSlotSx}>
                    <Typography variant="caption" sx={partsColHeaderCaptionInSlotSx}>Packing Slips</Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.slips}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, slips: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={partsHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.category} onResizeStart={(e) => startResize('category', e)}>
                <Box sx={partsColHeaderStackSx}>
                  <Box sx={partsColHeaderLabelSlotSx}>
                    <Typography variant="caption" sx={partsColHeaderCaptionInSlotSx}>Category</Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.category}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, category: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={partsHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.labels} onResizeStart={(e) => startResize('labels', e)}>
                <Box sx={partsColHeaderStackSx}>
                  <Box sx={partsColHeaderLabelSlotSx}>
                    <Typography variant="caption" sx={partsColHeaderCaptionInSlotSx}>Labels</Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.labels}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, labels: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={partsHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.supplier} onResizeStart={(e) => startResize('supplier', e)}>
                <Box sx={partsColHeaderStackSx}>
                  <Box sx={partsColHeaderLabelSlotSx}>
                    <Typography variant="caption" sx={partsColHeaderCaptionInSlotSx}>Supplier</Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.supplier}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, supplier: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={partsHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : parts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No parts found. Use New to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              parts.map((row) => (
                <TableRow
                  key={row.id}
                  id={`parts-row-${row.id}`}
                  hover
                  selected={selectedPart?.id === row.id}
                  onClick={() => setSelectedPart(row)}
                  onDoubleClick={() => openEditDialog(row)}
                  sx={{
                    cursor: 'pointer',
                    ...(highlightPartId != null && row.id === highlightPartId
                      ? { bgcolor: 'rgba(142, 166, 255, 0.22) !important' }
                      : {}),
                    ...(row.is_active === false ? { opacity: 0.88, bgcolor: 'action.hover' } : {})
                  }}
                >
                  <TableCell sx={{ width: colWidths.partNo }}>{row.part_number ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.desc, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.part_description ?? row.description ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.status }}>
                    {row.is_active === false ? (
                      <Chip label="Inactive" size="small" color="default" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                    ) : (
                      <Typography variant="caption" color="text.secondary">Active</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.qty }}>{row.quantity_on_hand != null ? row.quantity_on_hand : '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.min }}>{row.minimum_quantity != null ? row.minimum_quantity : '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.unitPrice }}>{formatCurrency(row.unit_price)}</TableCell>
                  <TableCell sx={{ width: colWidths.quotes }}>
                    {(row.quote_references ?? 0) > 0 ? (
                      <Button
                        size="small"
                        variant="text"
                        sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/quotes?part_id=${row.id}`);
                        }}
                      >
                        {row.quote_references}
                      </Button>
                    ) : (
                      0
                    )}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.orders }}>
                    {(row.order_references ?? 0) > 0 ? (
                      <Button
                        size="small"
                        variant="text"
                        sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/work-orders?part_id=${row.id}`);
                        }}
                      >
                        {row.order_references}
                      </Button>
                    ) : (
                      0
                    )}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.invoices }}>
                    {(row.invoice_references ?? 0) > 0 ? (
                      <Button
                        size="small"
                        variant="text"
                        sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/customer-invoices?part_id=${row.id}`);
                        }}
                      >
                        {row.invoice_references}
                      </Button>
                    ) : (
                      0
                    )}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.slips }}>
                    {(row.packing_slip_references ?? 0) > 0 ? (
                      <Button
                        size="small"
                        variant="text"
                        sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/packing-slips?part_id=${row.id}`);
                        }}
                      >
                        {row.packing_slip_references}
                      </Button>
                    ) : (
                      0
                    )}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.category }}>{row.category ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.labels }}>
                    <PartLabelChips
                      labels={row.labels}
                      onClickLabel={(lab) => setLabelFilter(String(lab.id))}
                    />
                  </TableCell>
                  <TableCell sx={{ width: colWidths.supplier }}>{row.supplier ?? '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          rowsPerPageOptions={[50, 100, 200]}
          count={partsTotal}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            const next = parseInt(e.target.value, 10);
            setRowsPerPage(next);
            setPage(0);
            try {
              window.localStorage.setItem('wm_parts_rows_per_page', String(next));
            } catch {
              // ignore quota / private mode
            }
          }}
          labelRowsPerPage="Rows per page"
        />
      </TableContainer>

      <NewPartModal
        open={newDialogOpen}
        onClose={() => setNewDialogOpen(false)}
        onCreated={() => {
          loadCategories();
          loadLabelsCatalog();
          loadParts();
        }}
      />

      <PartEditDialog
        open={editDialogOpen}
        part={selectedPart}
        onClose={() => setEditDialogOpen(false)}
        onSaved={() => {
          loadCategories();
          loadLabelsCatalog();
          loadParts();
        }}
      />

      <Dialog open={deleteConfirmOpen} onClose={() => !saving && setDeleteConfirmOpen(false)}>
        <DialogTitle>Deactivate Part</DialogTitle>
        <DialogContent>
          <Typography>
            Deactivate part{' '}
            <strong>{selectedPart?.part_number ?? selectedPart?.part_description ?? selectedPart?.description ?? selectedPart?.id}</strong>
            . This will only mark it inactive for inventory selection; existing Quotes, Orders, Invoices, and Packing Slips will remain unchanged.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeletePart} disabled={saving}>
            {saving ? 'Deactivating…' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={convertDialogOpen}
        onClose={() => !convertSaving && setConvertDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change part to service</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This creates a new service from the selected part, rewrites matching quote, order, and invoice lines to that service, clears part links on packing slip rows, and deactivates the part.
            Blocked if the part is still on purchase orders, receivers, product BOM, or inventory history.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Part:{' '}
            <strong>
              {selectedPart?.part_number ?? selectedPart?.id} — {selectedPart?.part_description ?? selectedPart?.description ?? '—'}
            </strong>
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            References — Quotes: {selectedPart?.quote_references ?? 0}, Orders: {selectedPart?.order_references ?? 0}, Invoices:{' '}
            {selectedPart?.invoice_references ?? 0}, Packing slips: {selectedPart?.packing_slip_references ?? 0}
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Service code (optional)"
            helperText="Leave blank to use the part number, or enter letters, numbers, and dashes only."
            value={convertServiceCode}
            onChange={(e) => setConvertServiceCode(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)} disabled={convertSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConvertToService} disabled={convertSaving}>
            {convertSaving ? 'Converting…' : 'Convert'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Parts;
